import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText, PieChart } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { api, formatApiError } from "@/lib/api";

export default function ClientReportsPage() {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadReport() {
      try {
        const response = await api.get("/reports/summary");
        setReport(response.data);
      } catch (requestError) {
        setError(formatApiError(requestError));
      }
    }
    loadReport();
  }, []);

  async function download(format) {
    try {
      const response = await api.get(`/reports/export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tagforce-client-report.${format}`;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="client-reports-shell">
      <AppSidebar activeItem="Reports" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page client-portal-page" data-testid="client-reports-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">SERVICE REPORTING</p>
              <h1>Reports</h1>
              <p className="page-subtitle">
                Review staffing activity and service coverage for your organization.
              </p>
            </div>
            <div className="client-report-export-actions" data-testid="client-report-export-actions">
              <button
                className="button button-secondary"
                data-testid="client-export-csv-button"
                type="button"
                onClick={() => download("csv")}
              >
                <Download size={15} /> CSV
              </button>
              <button
                className="button button-secondary"
                data-testid="client-export-excel-button"
                type="button"
                onClick={() => download("xlsx")}
              >
                <FileSpreadsheet size={15} /> Excel
              </button>
              <button
                className="button button-primary"
                data-testid="client-export-pdf-button"
                type="button"
                onClick={() => download("pdf")}
              >
                <FileText size={16} /> PDF
              </button>
            </div>
          </section>
          {error && (
            <p className="form-message error-message" data-testid="client-reports-error">
              {error}
            </p>
          )}
          {report && (
            <>
              <section className="client-report-metrics" data-testid="client-report-metrics">
                <article>
                  <small>Open service needs</small>
                  <strong data-testid="client-report-open-positions">
                    {report.metrics.open_positions}
                  </strong>
                </article>
                <article>
                  <small>Filled positions</small>
                  <strong data-testid="client-report-filled-positions">
                    {report.metrics.filled_positions}
                  </strong>
                </article>
                <article>
                  <small>Coverage rate</small>
                  <strong data-testid="client-report-fill-rate">{report.metrics.fill_rate}%</strong>
                </article>
                <article>
                  <small>Assignment activity</small>
                  <strong data-testid="client-report-assignment-activity">
                    {report.metrics.assignment_activity}
                  </strong>
                </article>
              </section>
              <section className="client-report-table" data-testid="client-report-table">
                <div className="section-heading">
                  <div>
                    <h2>Staffing activity</h2>
                    <p>{report.rows.length} requests for your organization.</p>
                  </div>
                  <PieChart size={19} />
                </div>
                <div className="request-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Request</th>
                        <th>Service</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Needed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row) => (
                        <tr data-testid={`client-report-row-${row.request_id}`} key={row.request_id}>
                          <td>{row.request_id}</td>
                          <td>{row.role}</td>
                          <td>{row.status}</td>
                          <td>{row.priority}</td>
                          <td>{row.date_needed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}